import ImageModal from '@activities/items/ItemActivity/components/ItemInfo/ImageModal';
import Column from '@components/Column';
import Divider from '@components/Divider';
import GiftNotes from '@components/gift/GiftNotes';
import { ENCOFFEE_GIFT_NOTES, GIFT_EXCHANGE_NOTE } from '@components/gift/GiftNotes/notes';
import Image from '@components/Image';
import Text from '@components/Text';
import colors from '@constants/colors';
import { ENCOFFEE_PROJECT_IDS } from '@constants/projects';
import styled from '@emotion/styled';
import useProjectId from '@hooks/useProjectId';
import { GiftItemRow } from '@models/gift';
import { useFlow } from '@stackflow/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import InfoIcon from './InfoIcon';
import SearchIcon from './SearchIcon';

interface Props {
    giftItem: GiftItemRow;
}

function ItemInfo({ giftItem }: Props) {
    const {
        id, status, name, imageUrl, description,
    } = giftItem;
    const isEncoffee = ENCOFFEE_PROJECT_IDS.includes(useProjectId());
    const { t } = useTranslation('gift');
    const { push } = useFlow();
    const [isImageOpen, setIsImageOpen] = useState(false);

    const handleMenuDetail = () => {
        push('GiftMenuDetailActivity', { giftItemId: id });
    };

    return (
        <Column as='section' fullWidth align='center'>
            <Content fullWidth spacing={3} align='center'>
                <ImageContainer>
                    <Image src={imageUrl} width={110} height={110} alt={name} style={{ objectFit: 'contain' }} />
                    {status === 'SOLD_OUT' && (
                        <SoldOut as='div' size='title' fontWeight='bold' color={colors.textSecondary}>{t('item.soldOut')}</SoldOut>
                    )}
                    <Tools>
                        <ToolButton type='button' aria-label={t('giftItem.menuDetail.title')} onClick={handleMenuDetail}>
                            <InfoIcon />
                        </ToolButton>
                        {imageUrl && (
                            <ToolButton type='button' aria-label={t('giftItem.zoomImage')}
                                onClick={() => setIsImageOpen(true)}>
                                <SearchIcon />
                            </ToolButton>
                        )}
                    </Tools>
                </ImageContainer>
                {imageUrl && <ImageModal images={[imageUrl]} isOpen={isImageOpen} setIsOpen={setIsImageOpen} />}
                <Text size='display' fontWeight='bold' align='center'>
                    {name}
                </Text>
                {description && (
                    <Text multiline align='center' size='body-short' fontWeight='regular'>
                        {description}
                    </Text>
                )}
            </Content>
            <Divider />
            <GiftNotes notes={isEncoffee ? ENCOFFEE_GIFT_NOTES : [GIFT_EXCHANGE_NOTE]} />
        </Column>
    );
}

const Content = styled(Column)`
    position: relative;
    padding: var(--spacing-8) var(--spacing-5) var(--spacing-4);
`;

const ImageContainer = styled.div`
    align-items: center;
    display: flex;
    justify-content: center;
    position: relative;
`;

const Tools = styled.div`
    display: flex;
    position: absolute;
    top: 0;
    right: 0;
    transform: translateY(-50%) translateX(50%);
    gap: var(--spacing-1);
    padding: 0 1px;
    border-radius: 12px;
    background: ${colors.gray2};
`;

const ToolButton = styled.button`
    display: flex;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
`;

const SoldOut = styled(Text)`
    background: linear-gradient(0deg, rgba(196, 131, 114, 0.16) 0%, rgba(196, 131, 114, 0.16) 100%), rgba(255, 255, 255, 0.65);
    line-height: 18px;
    padding: var(--spacing-2) var(--spacing-2-5);
    position: absolute;
    transform: rotate(-18deg);
`;

export default ItemInfo;
